// SNIGJ - Lawyer Dashboard with Alerts System
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Scale, Bell, Calendar, Clock, AlertTriangle, CheckCircle, FileText,
  Plus, Briefcase, Timer, CalendarDays, ChevronRight, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ALERT_TYPES = [
  { value: 'DEADLINE', label: { fr: 'Échéance', en: 'Deadline' }, color: 'bg-red-500' },
  { value: 'HEARING', label: { fr: 'Audience', en: 'Hearing' }, color: 'bg-blue-500' },
  { value: 'FILING', label: { fr: 'Dépôt', en: 'Filing' }, color: 'bg-yellow-500' },
  { value: 'PAYMENT', label: { fr: 'Paiement', en: 'Payment' }, color: 'bg-green-500' },
  { value: 'CUSTOM', label: { fr: 'Personnalisé', en: 'Custom' }, color: 'bg-purple-500' }
];

const PRIORITIES = [
  { value: 'LOW', label: { fr: 'Basse', en: 'Low' }, color: 'text-gray-500' },
  { value: 'MEDIUM', label: { fr: 'Moyenne', en: 'Medium' }, color: 'text-yellow-500' },
  { value: 'HIGH', label: { fr: 'Haute', en: 'High' }, color: 'text-orange-500' },
  { value: 'URGENT', label: { fr: 'Urgente', en: 'Urgent' }, color: 'text-red-500' }
];

const LawyerDashboardPage = () => {
  const { user, language } = useAuth();
  const [stats, setStats] = useState({ active_cases: 0, urgent_alerts: 0, upcoming_hearings: 0, pending_filings: 0 });
  const [alerts, setAlerts] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({
    title: '', description: '', alert_type: 'DEADLINE', priority: 'MEDIUM', due_date: '', case_id: ''
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsRes, alertsRes, casesRes] = await Promise.all([
        api.get('/lawyer/dashboard'),
        api.get('/lawyer/alerts'),
        api.get('/lawyer/my-cases')
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setHearings(alertsRes.data.upcoming_hearings || []);
      setCases(casesRes.data.cases || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async () => {
    try {
      await api.post('/lawyer/alerts', newAlert);
      toast.success(language === 'fr' ? 'Alerte créée' : 'Alert created');
      setShowAddAlert(false);
      setNewAlert({ title: '', description: '', alert_type: 'DEADLINE', priority: 'MEDIUM', due_date: '', case_id: '' });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await api.put(`/lawyer/alerts/${alertId}`, { status: 'DISMISSED' });
      toast.success(language === 'fr' ? 'Alerte rejetée' : 'Alert dismissed');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleCompleteAlert = async (alertId) => {
    try {
      await api.put(`/lawyer/alerts/${alertId}`, { status: 'COMPLETED' });
      toast.success(language === 'fr' ? 'Alerte complétée' : 'Alert completed');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const getDaysRemaining = (dueDate) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getAlertType = (type) => ALERT_TYPES.find(t => t.value === type) || ALERT_TYPES[0];
  const getPriority = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="lawyer-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Scale className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Tableau de Bord Avocat' : 'Lawyer Dashboard'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gérez vos dossiers, échéances et audiences' : 'Manage your cases, deadlines and hearings'}
            </p>
          </div>
          <Button onClick={() => setShowAddAlert(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            {language === 'fr' ? 'Nouvelle alerte' : 'New Alert'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_cases}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Dossiers actifs' : 'Active Cases'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={stats.urgent_alerts > 0 ? 'border-red-400' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.urgent_alerts}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Alertes urgentes' : 'Urgent Alerts'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.upcoming_hearings}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Audiences à venir' : 'Upcoming Hearings'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending_filings}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Dépôts en attente' : 'Pending Filings'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="alerts" className="space-y-4">
          <TabsList>
            <TabsTrigger value="alerts" className="gap-2">
              <Bell className="w-4 h-4" />
              {language === 'fr' ? 'Alertes' : 'Alerts'}
              {alerts.filter(a => a.status === 'ACTIVE').length > 0 && (
                <Badge variant="destructive" className="ml-1">{alerts.filter(a => a.status === 'ACTIVE').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="hearings" className="gap-2">
              <Calendar className="w-4 h-4" />
              {language === 'fr' ? 'Audiences' : 'Hearings'}
            </TabsTrigger>
            <TabsTrigger value="cases" className="gap-2">
              <Briefcase className="w-4 h-4" />
              {language === 'fr' ? 'Dossiers' : 'Cases'}
            </TabsTrigger>
          </TabsList>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Mes Alertes & Échéances' : 'My Alerts & Deadlines'}</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-20 skeleton rounded" />)}
                  </div>
                ) : alerts.filter(a => a.status === 'ACTIVE').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune alerte active' : 'No active alerts'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.filter(a => a.status === 'ACTIVE').map((alert) => {
                      const alertType = getAlertType(alert.alert_type);
                      const priority = getPriority(alert.priority);
                      const daysRemaining = getDaysRemaining(alert.due_date);
                      
                      return (
                        <div key={alert.alert_id} className={`p-4 border rounded-lg ${daysRemaining <= 3 ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-3 h-3 rounded-full mt-1.5 ${alertType.color}`} />
                              <div>
                                <h4 className="font-medium">{alert.title}</h4>
                                <p className="text-sm text-muted-foreground">{alert.description}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(alert.due_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                                  </span>
                                  <Badge variant="outline" className={priority.color}>
                                    {priority.label[language] || priority.label.fr}
                                  </Badge>
                                  {daysRemaining <= 0 ? (
                                    <Badge variant="destructive">{language === 'fr' ? 'En retard!' : 'Overdue!'}</Badge>
                                  ) : daysRemaining <= 3 ? (
                                    <Badge variant="destructive">{daysRemaining}j {language === 'fr' ? 'restants' : 'remaining'}</Badge>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleCompleteAlert(alert.alert_id)}>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDismissAlert(alert.alert_id)}>
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hearings Tab */}
          <TabsContent value="hearings">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Audiences à Venir' : 'Upcoming Hearings'}</CardTitle>
              </CardHeader>
              <CardContent>
                {hearings.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune audience programmée' : 'No scheduled hearings'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {hearings.map((hearing, idx) => (
                      <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{hearing.title || hearing.case_id}</h4>
                          <p className="text-sm text-muted-foreground">{hearing.court || 'Tribunal'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{new Date(hearing.hearing_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</p>
                          <p className="text-sm text-muted-foreground">{hearing.time || '09:00'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Mes Dossiers' : 'My Cases'}</CardTitle>
              </CardHeader>
              <CardContent>
                {cases.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucun dossier assigné' : 'No assigned cases'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cases.map((caseItem) => (
                      <div key={caseItem.case_id} className="p-4 border rounded-lg flex items-center justify-between hover:bg-muted/50 cursor-pointer">
                        <div>
                          <h4 className="font-medium">{caseItem.title || caseItem.case_number}</h4>
                          <p className="text-sm text-muted-foreground">{caseItem.case_id}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{caseItem.status}</Badge>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Alert Modal */}
        <Dialog open={showAddAlert} onOpenChange={setShowAddAlert}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{language === 'fr' ? 'Nouvelle Alerte' : 'New Alert'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                <Input 
                  value={newAlert.title}
                  onChange={(e) => setNewAlert({...newAlert, title: e.target.value})}
                  placeholder={language === 'fr' ? 'Ex: Dépôt conclusions' : 'Ex: File conclusions'}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <Select value={newAlert.alert_type} onValueChange={(v) => setNewAlert({...newAlert, alert_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ALERT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label[language] || type.label.fr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Priorité' : 'Priority'}</Label>
                  <Select value={newAlert.priority} onValueChange={(v) => setNewAlert({...newAlert, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label[language] || p.label.fr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Date d\'échéance' : 'Due Date'}</Label>
                <Input 
                  type="datetime-local"
                  value={newAlert.due_date}
                  onChange={(e) => setNewAlert({...newAlert, due_date: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                <Textarea 
                  value={newAlert.description}
                  onChange={(e) => setNewAlert({...newAlert, description: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddAlert(false)}>
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button onClick={handleCreateAlert} disabled={!newAlert.title || !newAlert.due_date}>
                  {language === 'fr' ? 'Créer' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LawyerDashboardPage;
